import { Box, Card, CardContent, CardActions, Button, Typography, Grid } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

export default function ContentManagementPage() {
  const navigate = useNavigate();
  useEffect(() => {
    const genId = () => `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const ensurePolicy = async (
      type: 'terms' | 'privacy' | 'tokushoho',
      title: string,
      blocks: Array<any>
    ) => {
      const ref = doc(db, 'policies', type);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, {
          title,
          blocks,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    };

    const seed = async () => {
      const termsBlocks = [
        {
          id: genId(),
          type: 'richtext',
          html:
            `<p>LessonMateは、教室と生徒の月謝のやり取りの手助けをするサービスです。</p>` +
            `<p>クレジットカード決済を無料でご利用いただけます。</p>` +
            `<p>なお、クレジットカード決済では、7.5%の決済手数料が売上から差し引かれます。</p>` +
            `<p>生徒には、毎月25日にクレジットカードに課金されます。</p>` +
            `<p>教室には、翌月5日に登録された口座に振り込まれます。</p>` +
            `<p>そのほか、250円の手数料を支払うことで、即座に口座へ入金することができます。</p>` +
            `<hr />` +
            `<p><strong>免責事項</strong></p>` +
            `<ul>` +
            `<li>本サービスの提供に関連してユーザーに生じた損害について、当社に故意または重過失がある場合を除き、当社は一切の責任を負いません。</li>` +
            `<li>当社は、事前の予告なく本サービスの内容を変更・中止することがあります。</li>` +
            `<li>外部サービス（決済事業者等）の障害・停止に起因して発生した遅延や不具合について、当社は責任を負いません。</li>` +
            `</ul>`,
        },
      ];

      const privacyBlocks = [
        {
          id: genId(),
          type: 'richtext',
          html:
            `<p>LessonMateは、生徒との連携のために、教室名、住所、連絡先を教室、生徒間で共有します。</p>` +
            `<p>また、クレジットカード決済を使用するために、Stripeに必要情報を連携するため、名前、生年月日をStripeと共有します。</p>` +
            `<hr />` +
            `<p><strong>利用目的</strong></p>` +
            `<p>取得した個人情報は、サービス提供、本人確認、決済処理、ユーザーサポートの目的で利用します。</p>` +
            `<p><strong>第三者提供</strong></p>` +
            `<p>法令に基づく場合を除き、本人の同意なく第三者に提供しません。決済に必要な範囲でStripeへ提供します。</p>` +
            `<p><strong>安全管理</strong></p>` +
            `<p>適切なアクセス制御とログ管理を実施し、個人情報の漏えい防止に努めます。</p>`,
        },
      ];

      const tokushohoBlocks = [
        {
          id: genId(),
          type: 'richtext',
          html:
            `<p><strong>事業者の名称</strong><br/>LessonMate 運営事業者</p>` +
            `<p><strong>運営責任者</strong><br/>LessonMate 担当者</p>` +
            `<p><strong>所在地</strong><br/>（住所を記載）</p>` +
            `<p><strong>連絡先</strong><br/>（電話番号・メールアドレスを記載）</p>` +
            `<p><strong>販売価格</strong><br/>クレジットカード決済の利用は無料</p>` +
            `<p><strong>商品代金以外の必要料金</strong><br/>クレジットカード決済手数料 7.5%（売上から差し引き）。即時入金をご希望の場合の即時入金手数料 250円</p>` +
            `<p><strong>支払方法</strong><br/>クレジットカード決済（Stripe）</p>` +
            `<p><strong>支払時期</strong><br/>毎月25日に課金</p>` +
            `<p><strong>役務提供の時期</strong><br/>決済完了後、直ちに利用可能</p>` +
            `<p><strong>返品・キャンセル</strong><br/>サービスの性質上、原則として返品・返金には応じられません。</p>`,
        },
      ];

      await ensurePolicy('terms', '利用規約', termsBlocks);
      await ensurePolicy('privacy', 'プライバシーポリシー', privacyBlocks);
      await ensurePolicy('tokushoho', '特定商取引法に基づく表記', tokushohoBlocks);
    };

    seed();
  }, []);
  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>コンテンツ管理</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>利用規約</Typography>
              <Typography variant="body2" color="text.secondary">
                アプリに表示される利用規約の内容を作成・更新します。
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="contained" onClick={() => navigate('/content/terms')}>編集</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>プライバシーポリシー</Typography>
              <Typography variant="body2" color="text.secondary">
                アプリに表示されるプライバシーポリシーの内容を作成・更新します。
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="contained" onClick={() => navigate('/content/privacy')}>編集</Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>特定商取引法に基づく表記</Typography>
              <Typography variant="body2" color="text.secondary">
                事業者情報・連絡先・役務提供条件などの法定表記を作成・更新します。
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="contained" onClick={() => navigate('/content/tokushoho')}>編集</Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}


